import { motion } from 'framer-motion';

import { useModal } from '../../hooks/modalContext';
import { RentCreditOption } from '../../utils/types';
import { useEffect, useState } from 'react';
import { usePoints } from '../../hooks/pointsContext';
import { getRentCreditOptions, redeemRentCredit } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { showToast } from '../../utils/showToast';
import { Icon } from '../../Icons/Icon';
import { ChevronLeft } from 'lucide-react';

const RedeemPoints = () => {
  const { setModal } = useModal();
  const { auth } = useAuth();
  const { points, refreshPoints } = usePoints();
  const [rentRedeem, setRentRedeem] = useState<RentCreditOption[]>();
  const [rentOption, setRentOption] = useState<RentCreditOption | null>(null);

  const handleRentOption = async () => {
    try {
      const response = await redeemRentCredit(
        auth?.data.token,
        rentOption?.rentCreditOptionId || ''
      );
      if (response.message === 'success') {
        showToast({
          message: `${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(
            rentOption?.amount || 0
          )} rent credit added. Points redeemed, credit auto-applies to next payment.`,
          type: 'success',
        });
        setTimeout(async () => {
          await refreshPoints();
        }, 1000);
      }
    } catch (error) {
      console.error('Error redeeming rent credit:', error);
    } finally {
      setModal(null, { refetchPaymentSummary: true });
    }
  };

  useEffect(() => {
    const fetchRentCreditOptions = async () => {
      try {
        const response = await getRentCreditOptions(auth?.data.token);
        setRentRedeem(response);
      } catch (error) {
        console.error('Error fetching rent credit options');
      }
    };
    fetchRentCreditOptions();
  }, []);

  return (
    <motion.div
      className='relative shadow-xl w-[433px] max-h-auto bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] rounded-[12px] flex flex-col overflow-auto border border-[#E9EDF9]'
      initial='hidden'
      animate='visible'
      exit='exit'
    >
      <div className='flex flex-col gap-9 items-center justify-center px-10 py-8'>
        {points && (
          <div className='w-full '>
            <div className='flex justify-between items-center w-full'>
              <ChevronLeft
                className='text-main cursor-pointer'
                onClick={() => {
                  setModal(null);
                  setModal('rewards', {
                    openTab: 'redeem',
                  });
                }}
              />

              <div className='flex items-center gap-2 bg-[var(--Primary-Color,#0055FF)] rounded-full px-3 py-2 h-9 cursor-pointer'>
                <Icon
                  name='wallet'
                  color='rgba(236,188,33,1)'
                  className='w-[22px] h-[22px]'
                  strokeWidth={0.5}
                />
                <span className='text-white text-lg'>
                  {Number(points?.data?.points?.totalAvailable ?? 0).toLocaleString('en-US')}
                </span>
              </div>
            </div>
          </div>
        )}
        <span className='text-main text-base'>
          Turn your TenantPay points into credit and knock some $$ off your next rent payment!
        </span>

        {rentRedeem && rentRedeem.length > 0 ? (
          <div className='grid grid-cols-2 max-[370px]:grid-cols-1 gap-4 w-full'>
            {rentRedeem.map((option) => {
              const isSelected = rentOption?.rentCreditOptionId === option.rentCreditOptionId;
              const isEligible =
                Number(option.points) <= Number(points?.data?.points?.totalAvailable ?? 0);

              return (
                <div
                  key={option.rentCreditOptionId}
                  onClick={() => {
                    if (isEligible) setRentOption(option);
                  }}
                  className={`group flex flex-col px-4 py-4 justify-center items-center gap-2 rounded-[8px] border transition-colors
                        ${isEligible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                        ${
                          isSelected
                            ? 'border-[rgba(138,146,171,0.30)] bg-[#F2F4F9]'
                            : isEligible
                            ? 'border-[#F7F8FA] bg-[rgba(236,238,243,0.40)] hover:border-[rgba(138,146,171,0.30)] hover:bg-[#F2F4F9]'
                            : 'border-[#F7F8FA] bg-[rgba(236,238,243,0.40)]'
                        }
                        }
                      `}
                >
                  <span
                    className={`text-base ${
                      isSelected
                        ? 'text-main'
                        : `${
                            isEligible ? 'text-[#8A92AB] group-hover:text-main' : 'text-[#8A92AB]'
                          }`
                    }`}
                  >
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(option.amount)}
                  </span>
                  <span
                    className={`text-base font-semibold ${
                      isSelected
                        ? 'text-main'
                        : `${
                            isEligible ? 'text-[#8A92AB] group-hover:text-main' : 'text-[#8A92AB]'
                          }`
                    }`}
                  >
                    {new Intl.NumberFormat('en-US').format(Number(option.points))} pts
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className='text-light/70'>No rent credit options available at the moment.</p>
        )}

        {rentOption && (
          <div className='flex justify-between w-full'>
            <span className='text-main text-base'>Redeem total</span>
            <span className='text-main font-semibold'>
              {new Intl.NumberFormat('en-US').format(Number(rentOption.points))} pts
            </span>
          </div>
        )}

        <div className='flex gap-5 w-full'>
          <button
            className='cancelBtn text-base'
            onClick={() => {
              setModal(null);
              setModal('rewards', {
                openTab: 'redeem',
              });
            }}
          >
            Cancel
          </button>
          <button
            className='submitBtn text-base'
            onClick={() => {
              handleRentOption();
            }}
            disabled={!rentOption}
          >
            Redeem
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export { RedeemPoints };
